import { Head, Link } from "@inertiajs/react"
import { CheckCircle2, ChevronLeft } from "lucide-react"
import { useTranslation } from "react-i18next"

import { CreatorModuleContent } from "@/components/learn/creator-module-content"
import { CreatorModuleMarkCompleteForm } from "@/components/learn/creator-module-mark-complete-form"
import AppLayout from "@/layouts/app-layout"
import { learnMasterclassPath } from "@/routes"
import type { CreatorLearnModuleShowProps } from "@/types/learn"

/**
 * OKS-602 — Creator-side module viewer
 * (`GET /learn/:masterclass_id/modules/:id`).
 *
 * Reachable ONLY for an unlocked (completed / in-progress) module — the
 * server 404s locked / out-of-order / cross-brand ids before this page
 * renders (`CreatorMasterclassModuleLoadable`). So there is no "locked"
 * state to render here.
 *
 * States (ux-states skill):
 *   - success (not completed) — content + sticky "Mark module as complete"
 *   - success (completed)     — content + static "Completed" indicator
 *   - empty                   — module with no media → "no content"
 *     affordance inside `CreatorModuleContent`
 *   - loading / error         — handled at the Inertia transition layer
 *     (parent) like the sibling Show page
 *
 * The mark-complete action mirrors the OKS-56 mobile sticky-bottom
 * pattern (fixed above the bottom-tab nav on <lg, inline on lg+).
 */
export default function CreatorLearnModuleShow({
  masterclass,
  module_,
  is_completed,
}: CreatorLearnModuleShowProps) {
  const { t } = useTranslation()

  return (
    <AppLayout>
      <Head title={module_.title} />
      <main className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-6 pb-32 lg:pb-6">
        <Link
          href={learnMasterclassPath(masterclass.id)}
          className="text-muted-foreground inline-flex items-center gap-1 self-start text-sm hover:underline focus:outline-none"
          data-testid="learn-module-back"
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
          {t("learn.module.back_to_masterclass", { title: masterclass.title })}
        </Link>

        <header className="flex flex-col gap-2">
          <h1
            className="text-2xl font-bold tracking-tight"
            data-testid="learn-module-title"
          >
            {module_.title}
          </h1>
        </header>

        <CreatorModuleContent module={module_} />

        {/* Action region — sticky on mobile, inline on lg+ (mirrors OKS-56).
            OKS-638: offset above the tab bar, which now reserves the iOS bottom
            safe-area inset under its 4rem row — keeps this CTA stacked above the
            grown bar; on non-inset devices calc() resolves to 4rem (old bottom-16). */}
        <div
          className="bg-background/95 supports-[backdrop-filter]:bg-background/85 fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-30 border-t p-3 backdrop-blur lg:static lg:inset-auto lg:bottom-auto lg:border-0 lg:bg-transparent lg:p-0"
          data-testid="learn-module-action-region"
        >
          {is_completed ? (
            <div
              role="status"
              className="bg-muted/50 flex items-center gap-3 rounded-md border p-4"
              data-testid="learn-module-completed-indicator"
            >
              <CheckCircle2
                className="size-5 shrink-0 text-emerald-600"
                aria-hidden="true"
              />
              <p className="text-sm font-medium">
                {t("learn.module.completed")}
              </p>
            </div>
          ) : (
            <CreatorModuleMarkCompleteForm
              masterclassId={masterclass.id}
              moduleId={module_.id}
              className="flex justify-center sm:justify-start"
            />
          )}
        </div>
      </main>
    </AppLayout>
  )
}
